import AppPageTitle from '@/components/AppPageTitle';
import AppSelect from '@/components/AppSelect';
import AppTable from '@/components/AppTable';
import AppTableQuery from '@/components/AppTableQuery';
import PATH from '@/config/paths/PATH';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTopicsAnswer } from '@/services/topic.service';
import useTopic from '@/redux/hooks/useTopic';
import { parseKeyword, safeJSONParse } from '@/utils';
import { formatDate, columnSorter, formatNumber } from '@/utils/formatters';
import useTopicOptionsQuery from '@/hooks/useTopicOptionsQuery';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { Col, Space } from 'antd';
import { getActiveBadge } from '@/utils/getActiveBadge';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';

const filterOptions = [
  {
    value: null,
    label: 'All',
  },
  {
    value: 1,
    label: 'Active',
  },
  {
    value: 0,
    label: 'Inactive',
  },
];

function TopicAnswer() {
  const navigate = useNavigate();
  // const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectedFilterTopic, setSelectedFilterTopic] = useState(null);
  const [queryValues, setQueryValues] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const offset = (pageIndex - 1) * pageSize + 1;
  const { setEditingTopicAnswerValues } = useTopic();

  const { data: topicsData } = useTopicOptionsQuery();

  // const { mutate: deleteAnswer, isPending } = useMutation({
  //   mutationFn: deleteTopicAnswer,
  //   onSuccess: () => {
  //     queryClient.invalidateQueries();
  //   },
  // });

  const {
    data: topicAnswersData,
    isLoading,
    refetch,
    isRefetching,
  } = usePaginatedQuery(
    () =>
      getTopicsAnswer({
        pageSize,
        offset,
        fullWordEn: queryValues?.searchValue,
        isActive: queryValues?.selectedFilter,
        topicId: queryValues?.selectedFilterTopic,
      }),
    ['topic-answers', pageSize, offset, queryValues]
  );

  const handleCreateTopic = () => navigate(PATH.TOPIC_ANSWER.CREATE);

  const onFilterChange = (val) => setSelectedFilter(val);
  const onTopicFilterChange = (val) => setSelectedFilterTopic(val);
  const handleQuery = () => {
    setQueryValues({
      searchValue,
      selectedFilter,
      selectedFilterTopic,
    });
    setPageIndex(1);
  };

  const handleReset = () => {
    setSearchValue(null);
    setSelectedFilter(null);
    setSelectedFilterTopic(null);
    setQueryValues(null);
    setPageIndex(1);
  };

  const handleOnRefresh = () => {
    refetch();
  };

  const handleEditTopic = (record) => {
    const sanitizedKeyword = safeJSONParse(record.key_word);
    setEditingTopicAnswerValues({
      ...record,
      key_word: parseKeyword(sanitizedKeyword.map((item) => item.core).join('')),
      key_word_unicode: sanitizedKeyword.map((item) => item.unicode).join('/_/'),
    });

    navigate(PATH.TOPIC_ANSWER.UPDATE.replace(':id', record.id));
  };

  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);
  const isTablet = useMediaQuery(`(max-width: ${mediaQueryPoints.mdPlus}px)`);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      fixed: !isMobile ? 'left' : '',
      sorter: columnSorter('id'),
      render: (value) => (value ? value : '-'),
    },
    {
      title: 'Topic',
      dataIndex: 'topic_name',
      key: 'topic_name',
      fixed: !isMobile ? 'left' : '',
      sorter: columnSorter('topic_name'),
      render: (value) => (value ? value : '-'),
    },
    {
      title: 'Number of words',
      dataIndex: 'number_words',
      key: 'number_words',
      sorter: columnSorter('number_words'),
      render: (value) => (value ? formatNumber(value) : '0'),
    },
    {
      title: 'Keyword',
      dataIndex: 'key_word',
      key: 'key_word',
      render: (key_word) => {
        // lấy core từ key_word nối lại thành chuỗi
        return key_word
          ? parseKeyword(safeJSONParse(key_word).map((item) => item.core)).join('')
          : '-';
      },
    },
    {
      title: 'Full Word (English)',
      dataIndex: 'full_word_en',
      key: 'full_word_en',
      sorter: columnSorter('full_word_en'),
      render: (value) => (value ? value : '-'),
    },
    {
      title: 'Full Word (Myanmar)',
      dataIndex: 'full_word_my',
      key: 'full_word_my',
      render: (value) => (value ? value : '-'),
    },
    {
      title: 'Crossword Config',
      dataIndex: 'crossword_config',
      key: 'crossword_config',
      render: (value) => (value ? value : '-'),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (is_active) => {
        return getActiveBadge(+is_active === 1, {
          activeText: 'Active',
          inactiveText: 'Inactive',
        });
      },
      sorter: columnSorter('is_active'),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => (date ? formatDate(date) : '-'),
      sorter: columnSorter('created_at', 'date'),
    },
  ];

  return (
    <Space direction="vertical" className="w-full">
      <AppPageTitle title="Topic Answers" />
      <AppTableQuery
        searchPlaceholder="Search full word English"
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleQuery={handleQuery}
        handleReset={handleReset}
        customFilter={
          <>
            <Col span={isMobile ? 24 : 6}>
              <AppSelect
                items={filterOptions}
                value={selectedFilter}
                placeholder="Status"
                title="Status"
                className="w-full"
                onChange={onFilterChange}
                label="Status"
                formItemClassName={'mb-0'}
              />
            </Col>
            <Col span={isMobile ? 24 : isTablet ? 8 : 6}>
              <AppSelect
                items={[
                  {
                    value: null,
                    label: 'All',
                  },
                  ...(topicsData?.dataRes || []).map((item, index) => {
                    return {
                      value: item.id,
                      label: item.name_en.toString(),
                    };
                  }),
                ]}
                className="w-full"
                placeholder="Topic name"
                title="Topic name"
                label={'Topic name'}
                formItemClassName={'mb-0'}
                value={selectedFilterTopic}
                onChange={onTopicFilterChange}
              />
            </Col>
          </>
        }
      />
      <AppTable
        columns={columns}
        hasRowAction
        dataSource={topicAnswersData?.dataRes}
        totalItems={topicAnswersData?.total}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setPageIndex(page);
        }}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
        }}
        loading={isLoading || isRefetching}
        onEditAction={handleEditTopic}
        // onDeleteAction={handleDeleteTopic}
        onCreate={handleCreateTopic}
        onRefresh={handleOnRefresh}
        // hasDeleteAction
      />
    </Space>
  );
}

export default TopicAnswer;
